export interface MarketVacancyCondition {
  id: number;
  position: number;
  kind: 'experience' | 'certification' | 'other';
  strength: 'eis' | 'wens';
  quote: string;
  label: string;
  min_years: number | null;
  min_count: number | null;
  scope_kind: 'function' | 'org_type' | 'sector' | 'tool' | 'activity' | null;
  scope_text: string | null;
  recency_years: number | null;
  vague: boolean;
  Certification_id: number | null;
}

export interface MarketVacancy {
  conditions?: MarketVacancyCondition[];
  conditions_extracted_at?: string | null;
  conditions_incomplete?: boolean;
  eligibility?: EligibilitySummary;
  /** Plan 3: set by the serializer only when the viewer may see the verdict and the tier puts the pair (or the aggregate) second. */
  match_demoted?: boolean;
  id: number;
  source: string; // 'Circle8' | 'Flextender' | 'Magnit' | 'Striive'
  source_vacancy_id: string;
  vacancy_id: string;
  title: string;
  company?: string;
  location?: string;
  description?: string;
  url?: string;
  skills_text?: string;
  hard_skills_ai?: string;
  certifications_ai?: string;
  years_experience?: string;
  posting_date?: string;
  deadline?: string;
  start_date?: string;
  end_date?: string;
  hours_per_week?: string;
  contract_duration?: string;
  tariff?: string;
  industry?: string;
  ai_summary?: string;
  Client_id?: number;
  Project_id?: number;
  /** Intake rows only: the tenant's own CRM client this Direct request is for (contract 3.5). */
  buyer_Client_id?: number | null;
  /** Intake rows only: the mail that created this Direct request. */
  intake_mail_id?: number | null;
  /** Intake rows only: the parser left fields empty or dropped one. */
  needs_details?: boolean;
  status: number;
  scraped_at?: string;
  enriched_at?: string;
  archived_at?: string;
  created_by?: number;
  created_at?: string;
  updated_at?: string;

  // Relations
  interests?: MarketVacancyInterest[];
  team_interests?: MarketVacancyInterest[];
  skills?: { id: number; name: string; category?: { name: string } }[];
  certifications?: { id: number; name: string; issuer?: string }[];
  developments?: { id: number; name: string }[];
  industries?: { id: number; name: string }[];
  domains?: { id: number; name: string }[];

  // Organization link (null = no canonical org, triggers client-picker on createDeal)
  Organization_id?: number | null;

  // CRM link
  crm_opportunity_id?: number | null;

  // Seen tracking (populated for list views)
  seen?: { id: number; User_id: number; MarketVacancy_id: number }[];
  is_new?: boolean;

  hidden?: { id: number; User_id: number; MarketVacancy_id: number }[];

  // Match score (populated for list views)
  match_score?: VacancyMatchScore;

  /**
   * The same opening posted on other platforms (#1197). Detail view only.
   *
   * Server-filtered: a canonical group can over-merge, so only siblings whose
   * title matches this one's are sent. An empty array therefore means "no
   * sibling we can vouch for", not "this opening is posted nowhere else".
   *
   * At most one row per platform, and never this vacancy's own platform: each
   * row is a distinct route to the opening, not a distinct posting. So the list
   * is safe to render as one chip per entry without deduplicating first.
   */
  group_members?: VacancySiblingPosting[];
}

/** One other platform's posting of the same opening. */
export interface VacancySiblingPosting {
  id: number;
  source: string;
  source_vacancy_id: string;
  url?: string | null;
  title: string;
  deadline?: string | null;
  canonical_group_id: number | null;
}

/**
 * Minimal user projection for avatar clusters (name + picture sources only).
 * Matches the columns teamInterestsLoader()/the match payloads select — kept
 * separate from the full User interface, which carries fields these payloads
 * don't load. Shared so the avatar helpers stay typed instead of `any`.
 */
export interface UserAvatarSource {
  id: number;
  name_first: string;
  name_last: string;
  username?: string;
  provider_avatar?: string | null;
  logo?: { preview?: string } | null;
}

export interface MarketVacancyInterest {
  id: number;
  MarketVacancy_id: number;
  User_id: number;
  interest_level: InterestLevel;
  created_by?: number;
  created_at?: string;
  updated_at?: string;

  // Relations
  vacancy?: MarketVacancy;
  user?: UserAvatarSource;
}

export enum InterestLevel {
  INTERESTED = 'interested',
  APPLIED = 'applied',
  WON = 'won',
  REJECTED = 'rejected',
}

export interface MarketVacancyStatistics {
  total: number;
  active: number;
  by_source: { [source: string]: number };
  by_company: { [company: string]: number };
  by_location: { [location: string]: number };
  my_interests: number;
  recent: number;
}

export interface MarketVacancyTabCounts {
  active: number;
  interests: number;
  hidden: number;
  inactive: number;
  team?: number;
}

export interface MarketVacancyFilters {
  status?: number;
  source?: string | string[];
  search?: string;
  with_interests?: boolean;
  sort_by?: string;
  sort_dir?: 'asc' | 'desc';
}

/** Text-only attribution of a matched user (best / selected match). Narrower
 *  than UserAvatarSource on purpose — these render a plain name link, no avatar,
 *  so no username/logo is carried. */
export interface MatchAttributionUser {
  id: number;
  name_first: string;
  name_last: string;
  provider_avatar?: string;
}

// Vacancy Match Score interfaces
export interface VacancyMatchScore {
  score: number | null;
  matched_skills_count: number | null;
  total_required_skills: number | null;
  matched_certs_count: number | null;
  total_required_certs: number | null;
  scoring_method: 'weighted' | 'no_score';
  needs_enrichment: boolean;
  /** Admin only: the aggregate's best match is a demoted pair because nobody meets the eisen. Score is null then. */
  best_is_fallback?: boolean;
  metadata?: ScoringMetadata;
  best_user?: MatchAttributionUser;
  selected_user?: MatchAttributionUser;
}

export interface ScoringMetadata {
  scoring_factors?: { [key: string]: ScoringFactorDetail };
  adjusted_weights?: { [key: string]: number };
  description_fit_warning?: boolean;
}

export interface ScoringFactorDetail {
  score: number;
  matched_count: number;
  total_required: number;
  match_details: MatchDetail[];
  applicable: boolean;
  notes?: string;
}

export interface MatchDetail {
  user_skill_name?: string;
  user_cert_name?: string;
  vacancy_skill_name?: string;
  name?: string;
  match_type: 'exact' | 'fuzzy' | 'experience_text' | 'category';
  confidence?: number;
  criticality?: string;
  is_primary?: boolean;
}

export interface MatchedSkill {
  user_skill_name: string;
  match_type: 'exact' | 'fuzzy';
  confidence: number;
}

export interface MatchedCert {
  user_cert_name: string;
  match_type: 'exact' | 'fuzzy';
  confidence: number;
}

export interface EligibilityReason { code: string; params: { [k: string]: number | null }; }
export interface EligibilityRow { id: number; title: string | null; company: string | null; from: number | null; to: number | null; note: string | null; }
export interface EligibilityCondition {
  id: number; label: string; kind: string;
  verdict: 'met' | 'not_met' | 'unknown' | 'not_checked' | 'other';
  reason: EligibilityReason | null; rows: EligibilityRow[];
}
export interface EligibilitySummary {
  verdict: 'eligible' | 'ineligible' | 'unknown' | 'not_checked';
  eisen_met: number; eisen_total: number;
  reason: EligibilityReason | null;
  conditions: EligibilityCondition[];
  gaps: { row_id: number; kind: string; title: string | null }[];
}

export interface MatchingUser {
  User_id: number;
  user: UserAvatarSource | null;
  score: number | null;
  matched_skills: MatchedSkill[];
  matched_certs: MatchedCert[];
  match_details?: ScoringMetadata;
  scoring_method: 'weighted' | 'no_score';
  profile_incomplete: boolean;
  eligibility?: EligibilitySummary;
  match_demoted?: boolean;
}

export interface MatchingUsersResponse {
  vacancy_id: number;
  match_score: VacancyMatchScore;
  matching_users: MatchingUser[];
}

export interface CreateManualVacancyPayload {
  title: string;
  source?: string;
  description?: string;
  company?: string;
  location?: string;
  url?: string;
  years_experience?: string;
  hours_per_week?: string;
  posting_date?: string;
  deadline?: string;
  start_date?: string;
  end_date?: string;
  contract_duration?: string;
  tariff?: string;
  industry?: string;
  Project_id?: number;
  skill_ids?: number[];
  certification_ids?: number[];
  development_ids?: number[];
  industry_ids?: number[];
}

export interface SuggestedVacancyFlagState {
  semantic_skill_fallback: boolean;
  hard_skills_evidence: boolean;
}

export interface SuggestedVacancy {
  id: number;
  MarketVacancy_id: number;
  User_id: number;
  Client_id: number;
  /** The serializer hides the key on a demoted pair. */
  score?: number | null;
  match_demoted?: boolean;
  matched_skills: MatchedSkill[];
  matched_certs: MatchedCert[];
  match_details?: ScoringMetadata;
  scoring_method: 'weighted' | 'no_score';
  profile_incomplete: boolean;
  vacancy: MarketVacancy;
  flag_state?: SuggestedVacancyFlagState;
  eligibility?: EligibilitySummary;
}
